import axios from 'axios';
import { AsyncAction } from '..';
import { Project } from '../../types/project';

type State = {
    active: { fetching: boolean; project?: Project };
    all: { fetching: boolean; projects: Project[] };
    error?: Error;
};

type Action =
    | { type: 'plan/projects/reset' }
    | { type: 'plan/projects/active'; payload?: { project?: Project }; error?: Error }
    | { type: 'plan/projects/all'; payload?: { projects: Project[] }; error?: Error };

const initialState: State = {
    active: { fetching: false },
    all: { fetching: false, projects: [] },
};

export const reducer = (state = initialState, action: Action): State => {
    switch (action.type) {
        case 'plan/projects/reset': {
            return { ...initialState };
        }
        case 'plan/projects/active': {
            if (action.payload) return { ...state, active: { fetching: false, ...action.payload } };
            if (action.error) return { ...state, active: { fetching: false }, error: action.error };
            return { ...state, active: { fetching: true } };
        }
        case 'plan/projects/all': {
            if (action.payload) return { ...state, all: { fetching: false, ...action.payload } };
            if (action.error) return { ...state, all: { fetching: false, projects: [] }, error: action.error };
            return { ...state, all: { fetching: true, projects: [] } };
        }
        default:
            return state;
    }
};

/**
 * Reset the reducer to the initial state.
 */
const reset = (): Action => ({ type: 'plan/projects/reset' });

/**
 * Fetch the active project of the plan in the store.
 *
 * @param rethrow throw the internal error
 */
const active = (rethrow = false): AsyncAction => async (dispatch, getState) => {
    const { plan } = getState();
    try {
        dispatch({ type: 'plan/projects/active' });
        if (!plan.plan) throw new Error('plan not in store');
        if (plan.plan.id === -1) return void dispatch({ type: 'plan/projects/active', payload: {} });
        const response = await axios.get<Project>(`/inspection-plans/${plan.plan.id}/next_inspection`);
        const project = response.data.id != undefined ? response.data : undefined;
        dispatch({ type: 'plan/projects/active', payload: { project } });
    } catch (error) {
        dispatch({ type: 'plan/projects/active', error });
        if (rethrow) throw error;
    }
};

/**
 * Fetch all projects of the plan in the store.
 *
 * @param rethrow throw the internal error
 */
const all = (rethrow = false): AsyncAction => async (dispatch, getState) => {
    const { plan } = getState();
    try {
        dispatch({ type: 'plan/projects/all' });
        if (!plan.plan) throw new Error('plan not in store');
        if (plan.plan.id === -1) return void dispatch({ type: 'plan/projects/all', payload: { projects: [] } });
        const response = await axios.get<{ results: Project[] }>(`/inspections/?inspection_plan=${plan.plan.id}`);
        const projects = response.data.results.sort((a, b) => +b.created_date - +a.created_date);
        dispatch({ type: 'plan/projects/all', payload: { projects } });
    } catch (error) {
        dispatch({ type: 'plan/projects/all', error });
        if (rethrow) throw error;
    }
};

export const creators = { reset, active, all };
